import * as React from 'react'

import { ContentItem, ProductBlock } from '@thg-commerce/enterprise-components'
import {
  i18n,
  useFormattableI18nProperty,
  useSiteConfig,
} from '@thg-commerce/enterprise-core'
import { RecommendationTrackingContext } from '@thg-commerce/enterprise-metrics/src/backend_event/types/tracking'
import {
  ProductPageData,
  ProductRecommendation,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Product/ProductPage'
import {
  FulfilmentMethod,
  InStockLocation,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import {
  findOption,
  getColour,
} from '@thg-commerce/enterprise-network/src/transformers/product/colorSwatch'
import { productPrice } from '@thg-commerce/enterprise-network/src/transformers/product/price'
import { ColourChoice } from '@thg-commerce/gravity-elements/Swatch'
import {
  SwatchShape,
  SwatchSize,
} from '@thg-commerce/gravity-elements/Swatch/types'
import { mq, spacing, styled, Text } from '@thg-commerce/gravity-theme'

import { useProductPage } from '../../../../context'

const Title = styled.h2`
  ${Text('large2', 'alternate')};
  text-align: center;
  margin-bottom: ${spacing(3)};
`

const RecommendedProductsContainer = styled.ul<{
  desktopWrap: boolean
  numberOfItems: number
}>`
  display: flex;
  flex-wrap: wrap;
  column-gap: 16px;
  list-style: none;
  width: 100;
  margin: auto;
  ${(props) => props.numberOfItems === 1 && 'justify-content: center;'}

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    flex-wrap: ${(props) => (props.desktopWrap ? 'wrap' : 'nowrap')};
    width: ${(props) =>
      props.numberOfItems <= 2
        ? props.desktopWrap
          ? 100
          : 50
        : props.numberOfItems * 25}%;
  }
`

const SectionContainer = styled.div`
  margin-top: ${spacing(3)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    margin-top: ${spacing(1)};
  }
`

const ProductBlockList = styled.li`
  width: calc(50% - ${spacing(1)});
  margin-bottom: ${spacing(1)};
`

export enum ProductOptionKeys {
  COLOUR = 'Colour',
  FLAVOUR = 'Flavour',
  SIZE = 'Size',
  SHADE = 'Shade',
}

export interface OtherCustomersBoughtProps {
  desktopWrap: boolean
  showBrandTitle?: boolean
  showBrandLogo?: boolean
  showPdpLinkWhenOutOfStock?: boolean
  trackingContext?: RecommendationTrackingContext
  product?: ProductPageData
}

export const OtherCustomersBought = (props: OtherCustomersBoughtProps) => {
  const { product } = useProductPage()
  const {
    enableClickAndCollect,
    alternateProductKeysForSwatches,
    departmentContentValueForSwatches,
  } = useSiteConfig()

  const i18nText = {
    otherCustomerBoughtTitle: i18n(
      'reviews.paginatedproductreviews.othercustomersbought.title',
    ),
    buyText: i18n('general.productbuynow.text'),
    quickBuyText: i18n('general.product.quickbuy.text'),
    buyAriaLabel: useFormattableI18nProperty('general.productbuynow.label'),
    reviewsStarsLabel: i18n('reviews.stars.label'),
    swatchTooltipUnavailable: i18n(
      'product.productoptions.swatch.tooltip.unavailable.text',
    ),
    swatchTooltipCloseButton: i18n(
      'product.productoptions.swatch.tooltip.close.text',
    ),
    rrpText: i18n('product.price.rrp.text'),
    soldOutText: i18n('general.productsoldout.text'),
  }

  const recommendations =
    props.product?.otherCustomersBought ?? product?.otherCustomersBought

  if (!recommendations || recommendations.length === 0) {
    return null
  }

  // TODO: REBUILD-3482 reduce duplication of variables
  // TODO: CSBOM-405 to revisit the swatches logic implementation
  const recommendedProducts = recommendations.map((recommendation, index) => {
    const mainProductSku = recommendation.sku
    const optionKeys = alternateProductKeysForSwatches
      ? [
          ProductOptionKeys.COLOUR,
          ProductOptionKeys.SIZE,
          ProductOptionKeys.SHADE,
        ]
      : [ProductOptionKeys.COLOUR, ProductOptionKeys.SHADE]

    const optionType = optionKeys
      .map((key) => findOption(recommendation, key))
      .find((option) => option)
    const getProductDepartment = (productContent) => {
      if (!alternateProductKeysForSwatches) {
        return null
      }
      return recommendation.options.find(
        (option) =>
          option.key === ProductOptionKeys.SIZE &&
          productContent
            ?.find((contentKey) =>
              alternateProductKeysForSwatches.includes(contentKey.key),
            )
            ?.value.stringValue.toLowerCase() ===
            departmentContentValueForSwatches?.toLowerCase(),
      )
    }
    const swatchProps = optionType?.choices.reduce(
      (swatches: NonNullable<ColourChoice[]>, colourChoice) => {
        const currentVariant = recommendation.variants.find(
          (variant) =>
            variant.inStock &&
            variant.choices.find((choice) => choice.key === colourChoice.key),
        )
        const href = currentVariant?.product?.url
        const productContent = recommendation.variants.find((variant) =>
          variant.content?.find((contentKey) =>
            contentKey.key.includes('colourHexCode'),
          ),
        )?.content
        const currentProduct = currentVariant && {
          sku: currentVariant.product?.sku,
          colour: getColour(currentVariant),
        }
        if (
          alternateProductKeysForSwatches &&
          colourChoice.colour === null &&
          getProductDepartment(productContent) &&
          (currentVariant?.inStockLocations?.includes(
            InStockLocation.Warehouse,
          ) ||
            currentVariant?.inStockLocations?.includes(InStockLocation.Store))
        ) {
          if (currentProduct && currentProduct.sku === mainProductSku) {
            colourChoice.colour = currentProduct.colour
          }
        }

        href &&
          colourChoice.colour &&
          swatches.push({
            href,
            colour: colourChoice.colour,
            ...colourChoice,
          })

        return swatches
      },
      [],
    )

    const reviews = recommendation.reviews
    const variantPrice = productPrice(recommendation as ProductRecommendation)
    const images = recommendation.images

    const productBlockProps = {
      content: recommendation.content as ContentItem[],
      sku: recommendation.sku.toString(),
      product: recommendation,
      externalIdentifier: recommendation.externalIdentifier?.toString(),
      brand: {
        name: recommendation.brand?.name,
        imageUrl: recommendation.brand?.imageUrl,
      },
      title: {
        value: recommendation.title,
      },
      url: props.trackingContext
        ? `${recommendation.url}?rctxt=${props.trackingContext}`
        : recommendation.url,
      review: reviews?.averageScore
        ? {
            starRating: reviews.averageScore,
            numberOfReviews: reviews.total,
            screenReaderOnlyText: `${reviews.averageScore} ${i18nText.reviewsStarsLabel}`,
          }
        : undefined,
      price: variantPrice
        ? {
            price: { defaultPrice: variantPrice.price },
            rrp: {
              defaultPrice: variantPrice.rrp ? variantPrice.rrp : undefined,
            },
            rrpText: i18nText.rrpText,
          }
        : undefined,
      image: {
        isAmp: false,
        urls: {
          largeProduct: images[0]?.largeProduct ?? '',
        },
        lazy: true,
      },
      swatch: swatchProps
        ? {
            shape: SwatchShape.CIRCLE,
            size: SwatchSize.Small,
            colours: swatchProps,
            i18nText: {
              unavailableText: i18nText.swatchTooltipUnavailable,
              closeButtonText: i18nText.swatchTooltipCloseButton,
            },
          }
        : undefined,
      // button: recommendation['inStock']
      //   ? {
      //       title: i18nText.buyText,
      //       ariaLabel: i18nText.buyAriaLabel(recommendation.title),
      //       quickBuyTitle: i18nText.quickBuyText,
      //       productInStock: recommendation['inStock'],
      //     }
      //   : {
      //       title: i18nText.soldOutText,
      //       quickBuyTitle: '',
      //       disabled: !showPdpLinkWhenOutOfStock,
      //     },
      showBrandTitle: props.showBrandTitle,
      fulfilmentMethodIconsProps: {
        enableClickAndCollect,
        inStock: recommendation.defaultVariant?.inStock || false,
        isCheckStock: recommendation.variants.some(
          (variant) => variant.isCheckStock,
        ),
        isOrderInStore: recommendation.variants.some(
          (variant) => variant.isOrderInStore,
        ),
        leadTime: recommendation.defaultVariant?.leadTime || undefined,
        isBookable: recommendation.defaultVariant?.isBookable || false,
        inStockLocations: recommendation.defaultVariant?.inStockLocations || [],
        weightGroups: recommendation.defaultVariant?.weightGroups || [],
        iconsAvailability: {
          homeDelivery: Boolean(
            recommendation.defaultVariant?.eligibleForFulfilmentMethods?.includes(
              FulfilmentMethod.HomeDelivery,
            ),
          ),
          storeAvailable: Boolean(
            recommendation.defaultVariant?.eligibleForFulfilmentMethods?.includes(
              FulfilmentMethod.CollectInStore,
            ),
          ),
          storeDelivery: Boolean(
            recommendation.defaultVariant?.eligibleForFulfilmentMethods?.includes(
              FulfilmentMethod.DeliverToStore,
            ),
          ),
        },
        i18nText: {
          clickAndCollect: {
            isAvailable: i18n('product.list.clickandcollect.text'),
            isNotAvailable: i18n('product.clickandcollect.unavailable.text'),
          },
          homeDelivery: {
            isAvailable: i18n('product.delivery.home.instock.text'),
            isNotAvailable: i18n('product.delivery.home.unavailable.text'),
            isOutOfStock: i18n('product.delivery.home.outofstock.text'),
            datedDelivery: i18n(
              'basket.item.fulfilment.leadtime',
              recommendation.defaultVariant?.leadTime?.toString(),
            ),
            nextDayDelivery: i18n('basket.item.fulfilment.nextdaydelivery'),
            oneManDelivery: i18n(
              'product.item.fulfilment.1man.nextdaydeliveryavailable',
            ),
            outOfGaugeDelivery: i18n(
              'product.item.fulfilment.outofgauge.nameddaydeliveryavailable',
            ),
            dynamicDelivery: i18n(
              `product.item.fulfilment.pdp.${recommendation.defaultVariant?.weightGroups?.[0]?.toLowerCase()}`,
            ),
          },
          storeDelivery: {
            isAvailable: i18n('product.delivery.store.available.text'),
          },
          orderInStore: {
            isAvailable: i18n(
              'product.item.fulfilment.orderinstore.available.text',
            ),
          },
        },
      },
      showBrandLogo: props.showBrandLogo,
    }

    return (
      <ProductBlockList key={index}>
        <ProductBlock {...productBlockProps} />
      </ProductBlockList>
    )
  })

  return (
    <SectionContainer>
      <Title>{i18nText.otherCustomerBoughtTitle}</Title>
      <RecommendedProductsContainer
        desktopWrap={props.desktopWrap}
        numberOfItems={recommendations.length}
      >
        {recommendedProducts}
      </RecommendedProductsContainer>
    </SectionContainer>
  )
}
